import React, { useState } from 'react';
import { Card, Table, Button, Space, Tag, Typography, Spin, Alert, Modal, Empty, Badge } from 'antd';
import { ShoppingCartOutlined, CheckCircleOutlined, ClockCircleOutlined, GiftOutlined, AppstoreAddOutlined, CreditCardOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import { useIntl } from 'react-intl';
import {
    useGetAvailableAddonsQuery,
    useGetPurchasedAddonsQuery,
    useGetAvailableCreditsForAddonsQuery,
    usePreviewAddonPurchaseQuery,
    IAddon,
    IOrganisationAddon,
    AddonType,
} from '../../services/nestjs-base-api';
import AddonCheckoutForm from './AddonCheckoutForm';

const { Text, Paragraph } = Typography;

interface OrganisationAddonsProps {
    organisationId: number;
}

const OrganisationAddons: React.FC<OrganisationAddonsProps> = ({ organisationId }) => {
    const { user } = useAuth();
    const intl = useIntl();
    const [selectedAddon, setSelectedAddon] = useState<IAddon | null>(null);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

    const { data: availableAddons, isLoading: loadingAvailable } = useGetAvailableAddonsQuery({
        jwtToken: user?.jwtToken || '',
        orgId: organisationId,
    });

    const { data: purchasedAddons, isLoading: loadingPurchased } = useGetPurchasedAddonsQuery({
        jwtToken: user?.jwtToken || '',
        orgId: organisationId,
    });

    const { data: creditsData } = useGetAvailableCreditsForAddonsQuery({
        jwtToken: user?.jwtToken || '',
        orgId: organisationId,
    });

    const { data: preview, isLoading: loadingPreview } = usePreviewAddonPurchaseQuery(
        {
            jwtToken: user?.jwtToken || '',
            orgId: organisationId,
            addonId: selectedAddon?.id || '',
        },
        { skip: !selectedAddon }
    );

    const handlePurchaseClick = (addon: IAddon) => {
        setSelectedAddon(addon);
        setIsPreviewModalOpen(true);
    };

    const handleCheckoutSuccess = () => {
        setIsPreviewModalOpen(false);
        setSelectedAddon(null);
        // Refetch purchased addons to show the new addon
        window.location.reload();
    };

    const formatPrice = (cents: number, currency: string = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
        }).format(cents / 100);
    };

    const getAddonTypeTag = (type: AddonType) => {
        const colors: Record<AddonType, string> = {
            one_time: 'blue',
            recurring: 'purple',
            usage_pack: 'cyan',
        };
        const labels: Record<AddonType, string> = {
            one_time: intl.formatMessage({ id: 'addons.type.oneTime', defaultMessage: 'One-Time' }),
            recurring: intl.formatMessage({ id: 'addons.type.recurring', defaultMessage: 'Recurring' }),
            usage_pack: intl.formatMessage({ id: 'addons.type.usagePack', defaultMessage: 'Usage Pack' }),
        };
        return <Tag color={colors[type]}>{labels[type]}</Tag>;
    };

    const availableColumns = [
        {
            title: intl.formatMessage({ id: 'addons.column.addon', defaultMessage: 'Add-on' }),
            dataIndex: 'name',
            key: 'name',
            render: (name: string, record: IAddon) => (
                <div>
                    <Text strong>{name}</Text>
                    {record.description && (
                        <Paragraph type="secondary" style={{ margin: 0, fontSize: '12px' }}>
                            {record.description}
                        </Paragraph>
                    )}
                </div>
            ),
        },
        {
            title: intl.formatMessage({ id: 'addons.column.type', defaultMessage: 'Type' }),
            dataIndex: 'addonType',
            key: 'addonType',
            render: (type: AddonType) => getAddonTypeTag(type),
        },
        {
            title: intl.formatMessage({ id: 'addons.column.price', defaultMessage: 'Price' }),
            dataIndex: 'priceCents',
            key: 'priceCents',
            render: (priceCents: number, record: IAddon) => (
                <Text strong>{formatPrice(priceCents, record.currency)} <Text type="secondary">{(record.currency || 'USD').toUpperCase()}</Text></Text>
            ),
        },
        {
            title: intl.formatMessage({ id: 'addons.column.billing', defaultMessage: 'Billing' }),
            dataIndex: 'billingPeriod',
            key: 'billingPeriod',
            render: (period: string | undefined, record: IAddon) =>
                record.addonType === 'one_time' ? (
                    <Tag color="default">{intl.formatMessage({ id: 'addons.billing.oneTime', defaultMessage: 'One-time' })}</Tag>
                ) : (
                    <Tag color="blue">{period || intl.formatMessage({ id: 'addons.billing.na', defaultMessage: 'N/A' })}</Tag>
                ),
        },
        {
            title: intl.formatMessage({ id: 'addons.column.actions', defaultMessage: 'Actions' }),
            key: 'actions',
            render: (_: any, record: IAddon) => (
                <Button
                    type="primary"
                    icon={<ShoppingCartOutlined style={{ marginRight: 4 }} />}
                    onClick={() => handlePurchaseClick(record)}
                >
                    {intl.formatMessage({ id: 'addons.purchase', defaultMessage: 'Purchase' })}
                </Button>
            ),
        },
    ];

    const purchasedColumns = [
        {
            title: intl.formatMessage({ id: 'addons.column.addon', defaultMessage: 'Add-on' }),
            key: 'addon',
            render: (_: any, record: IOrganisationAddon) => (
                <div>
                    <Text strong>{record.addon?.name || intl.formatMessage({ id: 'addons.unknown', defaultMessage: 'Unknown' })}</Text>
                    {record.addon?.description && (
                        <Paragraph type="secondary" style={{ margin: 0, fontSize: '12px' }}>
                            {record.addon.description}
                        </Paragraph>
                    )}
                </div>
            ),
        },
        {
            title: intl.formatMessage({ id: 'addons.column.type', defaultMessage: 'Type' }),
            key: 'type',
            render: (_: any, record: IOrganisationAddon) =>
                record.addon ? getAddonTypeTag(record.addon.addonType) : '-',
        },
        {
            title: intl.formatMessage({ id: 'addons.column.status', defaultMessage: 'Status' }),
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => {
                const color = status === 'active' ? 'green' : status === 'expired' ? 'orange' : 'red';
                const icon = status === 'active' ? <CheckCircleOutlined /> : <ClockCircleOutlined />;
                const statusLabel = intl.formatMessage({ id: `addons.status.${status}`, defaultMessage: status.toUpperCase() });
                return <Tag color={color} icon={icon}>{statusLabel}</Tag>;
            },
        },
        {
            title: intl.formatMessage({ id: 'addons.column.purchased', defaultMessage: 'Purchased' }),
            dataIndex: 'purchasedAt',
            key: 'purchasedAt',
            render: (date: string) => new Date(date).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            }),
        },
        {
            title: intl.formatMessage({ id: 'addons.column.expires', defaultMessage: 'Expires' }),
            dataIndex: 'expiresAt',
            key: 'expiresAt',
            render: (date: string | undefined) =>
                date ? new Date(date).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                }) : intl.formatMessage({ id: 'addons.never', defaultMessage: 'Never' }),
        },
    ];

    if (loadingAvailable || loadingPurchased) {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
            </div>
        );
    }

    const totalCredits = creditsData?.totalCents || 0;

    return (
        <div>
            {/* Account Credits Banner */}
            {totalCredits > 0 && (
                <Alert
                    message={
                        <Space>
                            <GiftOutlined />
                            <span>
                                {intl.formatMessage(
                                    { id: 'addons.creditsAvailable', defaultMessage: 'You have {amount} in reward credits available! These will be automatically applied to your next add-on purchase.' },
                                    { amount: <strong>{formatPrice(totalCredits)}</strong> }
                                )}
                            </span>
                        </Space>
                    }
                    type="success"
                    showIcon={false}
                    style={{ marginBottom: 24 }}
                />
            )}

            {/* Available Add-ons */}
            <Card
                title={
                    <Space>
                        <AppstoreAddOutlined />
                        <span>{intl.formatMessage({ id: 'addons.availableAddons', defaultMessage: 'Available Add-ons' })}</span>
                    </Space>
                }
                style={{ marginBottom: 24 }}
            >
                {availableAddons && availableAddons.length > 0 ? (
                    <>
                        <Table
                            dataSource={availableAddons}
                            columns={availableColumns}
                            rowKey="id"
                            pagination={false}
                        />
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                            {intl.formatMessage({ id: 'addons.plusTaxNote', defaultMessage: 'Prices exclude applicable taxes (e.g. GST for Australian customers)' })}
                        </Text>
                    </>
                ) : (
                    <Empty
                        description={intl.formatMessage({ id: 'addons.noAvailableAddons', defaultMessage: 'No add-ons available for your current plan' })}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                )}
            </Card>

            {/* Purchased Add-ons */}
            <Card
                title={
                    <Space>
                        <CheckCircleOutlined />
                        <span>{intl.formatMessage({ id: 'addons.yourAddons', defaultMessage: 'Your Add-ons' })}</span>
                        {purchasedAddons && purchasedAddons.length > 0 && (
                            <Badge count={purchasedAddons.filter(a => a.status === 'active').length} style={{ backgroundColor: '#52c41a' }} />
                        )}
                    </Space>
                }
            >
                {purchasedAddons && purchasedAddons.length > 0 ? (
                    <Table
                        dataSource={purchasedAddons}
                        columns={purchasedColumns}
                        rowKey="id"
                        pagination={false}
                    />
                ) : (
                    <Empty
                        description={intl.formatMessage({ id: 'addons.noPurchasedAddons', defaultMessage: "You haven't purchased any add-ons yet" })}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                )}
            </Card>

            {/* Purchase Checkout Modal */}
            <Modal
                title={
                    <Space>
                        <CreditCardOutlined />
                        <span>{intl.formatMessage({ id: 'addons.purchaseAddon', defaultMessage: 'Purchase Add-on' })}</span>
                    </Space>
                }
                open={isPreviewModalOpen}
                onCancel={() => {
                    setIsPreviewModalOpen(false);
                    setSelectedAddon(null);
                }}
                footer={null}
                width={550}
                destroyOnClose
            >
                {loadingPreview ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin />
                    </div>
                ) : preview ? (
                    <AddonCheckoutForm
                        organisationId={organisationId}
                        preview={preview}
                        onSuccess={handleCheckoutSuccess}
                        onCancel={() => {
                            setIsPreviewModalOpen(false);
                            setSelectedAddon(null);
                        }}
                    />
                ) : (
                    <Alert
                        message={intl.formatMessage({ id: 'addons.previewError', defaultMessage: 'Unable to load purchase preview' })}
                        type="error"
                    />
                )}
            </Modal>
        </div>
    );
};

export default OrganisationAddons;
